class CollectionFilters {
  constructor() {
    if (window.collectionFiltersInstance) {
      return window.collectionFiltersInstance;
    }

    this.drawer = document.getElementById('CollectionFiltersDrawer');
    this.overlay = document.querySelector('.collection-filters-overlay');
    this.filterForm = document.getElementById('collection-filters');
    this.productGrid = document.getElementById('product-grid');
    this.sortSelector = document.getElementById('sort-by');
    this.filterTriggerButton = document.querySelector('.collection-filters-nav__trigger');
    this.ajaxinate = null;
    this.isInitialized = false;
    
    // Add column state management
    this.columnButtons = document.querySelectorAll('.collection-filters-nav__cols-btn');
    this.currentColumns = this.getInitialColumnCount();

    window.collectionFiltersInstance = this;
    this.init();
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.bindEvents();
    this.initAjaxinate();
    this.setupFilterCountListeners();
    this.setupFilterKeyboardAccess(); // WCAG 2.1.1 keyboard accessibility
    this.updateActiveFilterGroupsCount();
    this.initPriceRangeFromURL();
    this.setupPriceRangeSliders();
    this.initColumnButtons();
    this.isInitialized = true;
  }

  // Get initial column count based on viewport width and user preferences
  getInitialColumnCount() {
    // Check for stored user preference first
    const storedColumns = localStorage.getItem('preferredColumns');
    if (storedColumns) {
      return parseInt(storedColumns);
    }
    
    // Fall back to viewport-based defaults
    const width = window.innerWidth;
    if (width >= 1024) return 4; // Desktop default
    return 2; // Mobile default
  }

  // Initialize column button states and handlers
  initColumnButtons() {
    if (!this.columnButtons.length) return;

    // Sync HTML state with JavaScript state first
    this.syncColumnButtonStates();

    // Set initial active state and aria-selected
    this.columnButtons.forEach(btn => {
      const btnColumns = parseInt(btn.textContent);
      if (btnColumns === this.currentColumns) {
        btn.classList.add('active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      }
      btn.addEventListener('click', this.handleColumnChange.bind(this));
    });

    // Apply initial column count
    this.updateProductGridColumns(this.currentColumns);
  }

  // Sync HTML aria-selected state with JavaScript state
  syncColumnButtonStates() {
    // If HTML has a different default than our calculated default, update HTML
    const htmlDefaultButton = document.querySelector('.collection-filters-nav__cols-btn[aria-selected="true"]');
    if (htmlDefaultButton) {
      const htmlDefaultColumns = parseInt(htmlDefaultButton.textContent);
      if (htmlDefaultColumns !== this.currentColumns) {
        // Update HTML to match our calculated default
        htmlDefaultButton.setAttribute('aria-selected', 'false');
        this.columnButtons.forEach(btn => {
          if (parseInt(btn.textContent) === this.currentColumns) {
            btn.setAttribute('aria-selected', 'true');
          }
        });
      }
    }
  }

  // Handle column button clicks
  handleColumnChange(event) {
    const newColumns = parseInt(event.target.textContent);
    if (newColumns === this.currentColumns) return;

    // Update active button state and aria-selected
    this.columnButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
    event.target.classList.add('active');
    event.target.setAttribute('aria-selected', 'true');

    // Update state and grid
    this.currentColumns = newColumns;
    this.updateProductGridColumns(newColumns);

    // Store preference in localStorage
    localStorage.setItem('preferredColumns', newColumns);
  }

  // Update product grid columns
  updateProductGridColumns(columns) {
    const productGrid = document.querySelector('.product-grid');
    if (!productGrid) return;

    // Remove existing column classes
    productGrid.classList.remove('grid-cols-2', 'grid-cols-4');
    
    // Update grid template columns
    productGrid.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    productGrid.setAttribute("data-columns", columns);
  }

  bindEvents() {
    // Drawer open/close handlers
    document.querySelectorAll('[data-action="open-drawer"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.openDrawer();
      });
    });

    document.querySelectorAll('[data-action="close-drawer"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDrawer();
      });
    });

    document.querySelectorAll('[data-action="clear-filters"]').forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.handleClearFilters(event);
      });
    });

    // Filter form submission
    if (this.filterForm) {
      this.filterForm.addEventListener('submit', this.handleSubmit.bind(this));
      
      // Add clear filters handler with stopPropagation
      const clearButton = this.filterForm.querySelector('[data-action="clear-filters"]');
      if (clearButton) {
        clearButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.handleClearFilters(event);
        });
      }
    }

    // Close drawer on escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    // Add sort change handler
    if (this.sortSelector) {
      this.sortSelector.addEventListener('change', this.handleSort.bind(this));
    }
  }

  openDrawer() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Update aria-expanded on trigger button
    if (this.filterTriggerButton) {
      this.filterTriggerButton.setAttribute('aria-expanded', 'true');
    }
    
    // Store the element that triggered the drawer
    this.previousFocus = document.activeElement;
    
    // Focus the drawer title
    const drawerTitle = this.drawer.querySelector('#filter-drawer-title');
    if (drawerTitle) {
      drawerTitle.setAttribute('tabindex', '-1');
      drawerTitle.focus();
    }
    
    // Trap focus within drawer
    this.trapFocus();
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-active');
    document.body.style.overflow = '';


    // Update aria-expanded on trigger button
    if (this.filterTriggerButton) {
      this.filterTriggerButton.setAttribute('aria-expanded', 'false');
    }
    
    // Remove focus trap
    this.releaseFocus();
    
    // Return focus to the element that opened the drawer
    if (this.previousFocus && this.previousFocus.focus) {
      this.previousFocus.focus();
    }
  }

  trapFocus() {
    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.focusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    };

    this.drawer.addEventListener('keydown', this.focusHandler);
  }

  releaseFocus() {
    if (this.focusHandler) {
      this.drawer.removeEventListener('keydown', this.focusHandler);
      this.focusHandler = null;
    }
  }

  initAjaxinate() {
    // Always destroy existing instance first
    if (this.ajaxinate) {
      this.ajaxinate.destroy();
      this.ajaxinate = null;
    }

    // Remove any existing event listeners
    document.removeEventListener('scroll', this.checkIfPaginationInView);
    window.removeEventListener('resize', this.checkIfPaginationInView);
    window.removeEventListener('orientationchange', this.checkIfPaginationInView);

    // Only initialize if we have both container and pagination elements
    const container = document.querySelector('#AjaxinateLoop');
    const pagination = document.querySelector('#AjaxinatePagination');
    
    if (!container || !pagination) {
      return;
    }

    this.ajaxinate = new Ajaxinate({
      container: '#AjaxinateLoop',
      pagination: '#AjaxinatePagination',
      loadingText: 'Loading more products...',
      callback: () => {
        // Optional: Add any callback functionality after new products are loaded
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const formData = new FormData(this.filterForm);
    const searchParams = new URLSearchParams(formData);

    // Retain the sort parameter if it exists
    const currentParams = new URLSearchParams(window.location.search);
    const sortParam = currentParams.get('sort_by');
    if (sortParam) {
      searchParams.set('sort_by', sortParam);
    }

    const url = `${window.location.pathname}?${searchParams.toString()}`;

    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update URL and reinitialize price range to match new URL
      window.history.pushState({ path: url }, '', url);
      this.initPriceRangeFromURL();
      
      // Update the filter count after URL is updated
      this.updateActiveFilterGroupsCount();
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
      
      // Close drawer after applying filters
      this.closeDrawer();
      
    } catch (error) {
      console.error('Error updating filters:', error);
    }
  }

  async handleClearFilters(event) {
    event.preventDefault();
    
    const classes = event.target.classList
    const isDrawerButton = classes.contains('reset-button')

    // Clear all checkboxes
    this.filterForm.querySelectorAll('input[type="checkbox"]').forEach(input => {
      input.checked = false;
    });

    // Reset all filter counts
    document.querySelectorAll('[data-filter-count]').forEach(countSpan => {
      countSpan.textContent = '';
    });

    const url = `${window.location.pathname}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update product grid
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update URL and reinitialize price range to match new URL
      window.history.pushState({ path: url }, '', url);
      this.initPriceRangeFromURL();
      
      // Update the filter groups count AFTER URL is cleared
      this.updateActiveFilterGroupsCount();
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
      
      // Close drawer
      if (!isDrawerButton && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
      }
      
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
  }

  async handleSort(event) {
    const selectedValue = event.target.value;
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('sort_by', selectedValue);
    const url = `${window.location.pathname}?${currentParams.toString()}`;

    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newProductGrid = doc.getElementById('product-grid');
      if (newProductGrid) {
        // Destroy current Ajaxinate instance before updating HTML
        if (this.ajaxinate) {
          this.ajaxinate.destroy();
        }
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }
      
      window.history.pushState({ path: url }, '', url);
      
      // Reinitialize Ajaxinate after HTML is updated
      this.initAjaxinate();
    } catch (error) {
      console.error('Error updating sort:', error);
    }
  }

  setupFilterCountListeners() {
    const filterGroups = document.querySelectorAll('.filter-group');

    filterGroups.forEach(group => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      const countSpan = group.querySelector('[data-filter-count]');

      if (!checkboxes.length || !countSpan) return;

      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length;
          countSpan.textContent = checkedCount > 0 ? `(${checkedCount})` : '';
          this.updateActiveFilterGroupsCount();
        });
      });
    });
  }

  // Setup keyboard accessibility for filter options - WCAG 2.1.1
  setupFilterKeyboardAccess() {
    const filterOptions = document.querySelectorAll('.filter-option');

    filterOptions.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const label = option.querySelector('label') || option;

      if (!checkbox) return;

      // Make filter option focusable
      if (!checkbox.disabled) {
        checkbox.setAttribute('tabindex', '0');
      }

      // Handle keyboard interaction on the option container
      option.addEventListener('keydown', (e) => {
        // Space or Enter key should toggle the checkbox
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          
          if (!checkbox.disabled) {
            checkbox.checked = !checkbox.checked;
            
            // Trigger change event for form processing
            const changeEvent = new Event('change', { bubbles: true });
            checkbox.dispatchEvent(changeEvent);
            
            // Announce change to screen readers
            this.announceFilterChange(checkbox);
          }
        }
      });

      // Ensure click events work properly
      label.addEventListener('click', (e) => {
        if (!checkbox.disabled) {
          // The click will naturally toggle the checkbox
          setTimeout(() => this.announceFilterChange(checkbox), 50);
        }
      });
    });
  }

  // Announce filter changes to screen readers
  announceFilterChange(checkbox) {
    const filterName = checkbox.getAttribute('aria-label') || checkbox.value;
    const isChecked = checkbox.checked;
    const message = `${filterName} filter ${isChecked ? 'selected' : 'deselected'}`;
    
    // Use existing announcement system if available
    if (window.announceMessage) {
      window.announceMessage(message);
    } else {
      // Create temporary live region for announcement
      let liveRegion = document.getElementById('filter-announcements');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'filter-announcements';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'false');
        liveRegion.className = 'visually-hidden';
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        document.body.appendChild(liveRegion);
      }
      
      liveRegion.textContent = message;
    }
  }

  updateActiveFilterGroupsCount() {
    if (!this.filterTriggerButton) return;

    // Count active checkbox groups
    const activeGroups = Array.from(document.querySelectorAll('.filter-group')).filter(group => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]:checked');
      return checkboxes.length > 0;
    }).length;

    // Check if price filter is active
    const urlParams = new URLSearchParams(window.location.search);
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');
    const isPriceFilterActive = minPrice || maxPrice;

    // Add price filter to total count if active
    const totalActiveFilters = activeGroups + (isPriceFilterActive ? 1 : 0);

    this.filterTriggerButton.textContent = `Filter${totalActiveFilters > 0 ? `s (${totalActiveFilters})` : ''} +`;
  }

  setupPriceRangeSliders() {
    const containers = document.querySelectorAll('.price-range-container');
    
    containers.forEach(container => {
      const minInput = container.querySelector('.price-range-min-input');
      const maxInput = container.querySelector('.price-range-max-input');
      const minLabel = container.querySelector('.price-range-min-label');
      const maxLabel = container.querySelector('.price-range-max-label');
      const minHidden = container.querySelector('[data-price-range-min]');
      const maxHidden = container.querySelector('[data-price-range-max]');
      const sliderRange = container.querySelector('.slider-range');

      function updateSlider() {
        const min = parseFloat(minInput.min);
        const max = parseFloat(minInput.max);
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);

        const leftPercent = ((minVal - min) / (max - min)) * 100;
        const rightPercent = ((maxVal - min) / (max - min)) * 100;

        sliderRange.style.left = `${leftPercent}%`;
        sliderRange.style.width = `${rightPercent - leftPercent}%`;

        minLabel.style.left = `${leftPercent}%`;
        minLabel.style.transform = 'translateX(-50%)';
        
        maxLabel.style.left = `${rightPercent}%`;
        maxLabel.style.transform = 'translateX(-50%)';
      }

      function updateValues(e) {
        const minValue = parseFloat(minInput.value);
        const maxValue = parseFloat(maxInput.value);

        if (this === minInput && minValue > maxValue) {
          minInput.value = maxValue;
        }
        
        if (this === maxInput && maxValue < minValue) {
          maxInput.value = minValue;
        }

        minHidden.value = minInput.value;
        maxHidden.value = maxInput.value;

        const minFormatted = formatMoney(Math.round(minInput.value * 100));
        const maxFormatted = formatMoney(Math.round(maxInput.value * 100));

        minLabel.textContent = minFormatted;
        maxLabel.textContent = maxFormatted;

        // Update screen reader context for current range
        const contextElement = container.querySelector(`#price-range-context-${container.getAttribute('data-index')}`);
        if (contextElement) {
          contextElement.textContent = `Current price range: ${minFormatted} to ${maxFormatted}`;
        }

        // Announce value changes for screen readers - WCAG 4.1.2
        if (this === minInput) {
          announceSliderChange('Minimum price', minFormatted);
        } else if (this === maxInput) {
          announceSliderChange('Maximum price', maxFormatted);
        }

        updateSlider();
      }

      // Function to announce slider value changes using the specific live regions
      function announceSliderChange(sliderType, value) {
        const containerIndex = container.getAttribute('data-index') || '1';
        let liveRegion;
        
        if (sliderType === 'Minimum price') {
          liveRegion = document.getElementById(`price-min-live-${containerIndex}`);
        } else if (sliderType === 'Maximum price') {
          liveRegion = document.getElementById(`price-max-live-${containerIndex}`);
        }
        
        if (liveRegion) {
          // Update the live region content to announce the new value
          const announcementSpan = liveRegion.querySelector(`.price-${sliderType === 'Minimum price' ? 'min' : 'max'}-announcement`);
          if (announcementSpan) {
            announcementSpan.textContent = `${sliderType}: ${value}`;
          }
        } else {
          // Fallback to generic announcement if live regions not found
          let fallbackRegion = document.getElementById('slider-announcements');
          if (!fallbackRegion) {
            fallbackRegion = document.createElement('div');
            fallbackRegion.id = 'slider-announcements';
            fallbackRegion.setAttribute('aria-live', 'polite');
            fallbackRegion.setAttribute('aria-atomic', 'true');
            fallbackRegion.className = 'visually-hidden';
            fallbackRegion.style.position = 'absolute';
            fallbackRegion.style.left = '-10000px';
            fallbackRegion.style.width = '1px';
            fallbackRegion.style.height = '1px';
            fallbackRegion.style.overflow = 'hidden';
            document.body.appendChild(fallbackRegion);
          }
          
          // Clear and announce the new value
          fallbackRegion.textContent = '';
          setTimeout(() => {
            fallbackRegion.textContent = `${sliderType} set to ${value}`;
          }, 100);
        }
      }

      minInput.addEventListener('input', updateValues);
      maxInput.addEventListener('input', updateValues);

      updateSlider();
    });
  }

  initPriceRangeFromURL() {
    if (!this.filterForm) return;

    const urlParams = new URLSearchParams(window.location.search);
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');

    this.filterForm.querySelectorAll('.price-range-container').forEach(container => {
      const minInput = container.querySelector('.price-range-min-input');
      const maxInput = container.querySelector('.price-range-max-input');
      const minHidden = container.querySelector('[data-price-range-min]');
      const maxHidden = container.querySelector('[data-price-range-max]');
      const minLabel = container.querySelector('.price-range-min-label');
      const maxLabel = container.querySelector('.price-range-max-label');
      
      if (minPrice && minInput && minHidden && minLabel) {
        minInput.value = minPrice;
        minHidden.value = minPrice;
        minLabel.textContent = formatMoney(Math.round(minPrice * 100));
      }
      
      if (maxPrice && maxInput && maxHidden && maxLabel) {
        maxInput.value = maxPrice;
        maxHidden.value = maxPrice;
        maxLabel.textContent = formatMoney(Math.round(maxPrice * 100));
      }
      
      if (minInput) minInput.dispatchEvent(new Event('input'));
      if (maxInput) maxInput.dispatchEvent(new Event('input'));
    });
  }
}

// Single initialization point
document.addEventListener('DOMContentLoaded', () => {
  window.collectionFiltersInstance = new CollectionFilters();
});

// Format a CENTS amount in the active presentment currency (Shopify.currency.active,
// e.g. "USD"/"CAD" from content_for_header). Intl replaces the old Shopify.formatMoney
// path, which never loaded here — so labels stay correct once a non-USD market is active.
// currencyDisplay: keep in sync with the shop's CAD money_format once a market exists —
// 'narrowSymbol' => "$10.00" (matches a bare-$ page), 'symbol' => "CA$10.00", 'code' => "CAD 10.00".
function formatMoney(cents) {
  var dollars = cents / 100;
  var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol'
    }).format(dollars);
  } catch (e) {
    return '$' + dollars.toFixed(2);
  }
}